import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface SegmentedProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  /** Stretch to the full container width with equal-width segments (avoids
   *  horizontal overflow when 3+ tabs won't fit a narrow phone). */
  block?: boolean;
}

/** Pill segmented control (filter tabs). */
export function Segmented<T extends string>({ options, value, onChange, size = 'md', block = false }: SegmentedProps<T>) {
  return (
    <div
      role="tablist"
      style={{
        display: block ? 'flex' : 'inline-flex',
        width: block ? '100%' : undefined,
        gap: 3,
        padding: 3,
        background: 'var(--paper-2)',
        borderRadius: 99,
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn('th')}
            style={{
              border: 'none',
              cursor: 'pointer',
              borderRadius: 99,
              fontFamily: 'var(--font)',
              fontWeight: 500,
              fontSize: size === 'sm' ? 13 : 14,
              padding: size === 'sm' ? '6px 12px' : '8px 15px',
              color: active ? 'var(--ink)' : 'var(--ink-3)',
              background: active ? 'var(--card)' : 'transparent',
              boxShadow: active ? 'var(--sh-sm)' : 'none',
              transition: 'color .16s ease, background .16s ease',
              // block mode: each tab shares space and clips gracefully rather
              // than pushing the control past the screen edge.
              flex: block ? '1 1 0' : undefined,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
